import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useSettings } from '../context/SettingsContext';
import api from '../utils/api';

export default function ContactPage() {
  const { s } = useSettings();
  const [form, setForm]   = useState({ name:'', email:'', phone:'', subject:'', message:'' });
  const [msg, setMsg]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setMsg('');
    try {
      await api.post('/contact', form);
      setMsg('success');
      setForm({ name:'', email:'', phone:'', subject:'', message:'' });
    } catch { setMsg('error'); }
    finally { setLoading(false); }
  };

  const INFO = [
    { icon: MapPin, label: 'Address',   value: s('contact_address', 'Plot 123, Maitama District, Abuja, FCT, Nigeria') },
    { icon: Phone,  label: 'Phone',     value: s('contact_phone', '+234 800 000 0000'), href: `tel:${s('contact_phone','')}` },
    { icon: Mail,   label: 'Email',     value: s('contact_email', 'info@saaingo.org'), href: `mailto:${s('contact_email','')}` },
    { icon: Clock,  label: 'Hours',     value: 'Mon – Fri: 9am – 5pm WAT' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">Get in Touch</span>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">Contact Us</h1>
            <p className="text-white/60 text-lg max-w-xl mx-auto">We'd love to hear from you. Reach out about programs, partnerships, volunteering, or anything else.</p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-5 gap-8">
            {/* Info sidebar */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
                <h3 className="font-display font-semibold text-gray-900 mb-5">Get in Touch</h3>
                <div className="space-y-5">
                  {INFO.map(i => (
                    <div key={i.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-royal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i.icon size={18} className="text-royal-800" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-0.5">{i.label}</div>
                        {i.href
                          ? <a href={i.href} className="text-sm text-royal-800 font-medium hover:underline">{i.value}</a>
                          : <div className="text-sm text-gray-700">{i.value}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-hero-gradient rounded-2xl p-6 text-white">
                <h4 className="font-display font-bold mb-3">Connect With Us</h4>
                <p className="text-white/60 text-sm mb-4">Follow us for program updates, impact stories, and events.</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { label: 'Twitter',   href: s('social_twitter','#') },
                    { label: 'Facebook',  href: s('social_facebook','#') },
                    { label: 'Instagram', href: s('social_instagram','#') },
                    { label: 'LinkedIn',  href: s('social_linkedin','#') },
                  ].map(soc => (
                    <a key={soc.label} href={soc.href} target="_blank" rel="noreferrer"
                      className="px-3 py-1.5 glass rounded-full text-xs font-semibold hover:border-gold-400/50 transition-colors">
                      {soc.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-gold-50 border border-gold-200 rounded-2xl p-5">
                <h4 className="font-semibold text-gray-900 mb-2">Emergency / Urgent?</h4>
                <p className="text-sm text-gray-500 mb-3">For urgent program matters, reach us on WhatsApp.</p>
                <a href={`https://wa.me/${s('whatsapp_number','2348000000000')}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:bg-[#20bf5a] transition-colors">
                  💬 WhatsApp Us
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-7">
                <h3 className="font-display font-semibold text-gray-900 mb-6">Send us a Message</h3>

                {msg === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-emerald-600" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-500 mb-6">We'll respond within 1-2 business days.</p>
                    <button onClick={() => setMsg('')} className="btn-royal px-6 py-2.5 text-sm">Send Another</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {msg === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">Something went wrong. Please try again.</div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Full Name *</label>
                        <input type="text" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
                        <input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                        <input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Subject *</label>
                        <select value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white">
                          <option value="">Select subject...</option>
                          {['General Inquiry','Partnership','Volunteering','Donation','Programs','Media / Press','Other'].map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Message *</label>
                      <textarea rows={6} value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} required
                        placeholder="Tell us how we can help..."
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all resize-none" />
                    </div>
                    <button type="submit" disabled={loading}
                      className="w-full btn-royal justify-center py-3.5 disabled:opacity-60 text-base">
                      {loading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send Message</>}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
